import { ActionContext, ActionTree } from "vuex";
import { RootState } from "@/store/state";
import { RootMutations } from "@/store/mutations";
import { log } from "@/core/logger";
import { GET, PUT, DELETE } from "@/core/http/methods";
import { WorkflowState } from "@/models/workflow-state";
import { IHeatmapConfig } from "@/models/heatmap";
import { SystemMode } from "@/models/system-mode";
import { constructAllLabels } from "@/store/logic";
import { reportsActions } from "@/store/reports/types";
import { ISlide } from "@/models/slide";
import { EventBus } from "@/eventBus";
import { ActiveView } from "@/models/active-view";

type Context = ActionContext<RootState, RootState>;

export enum RootActions {
  // Core

  GET_OR_FETCH_RESOURCE = "GET_OR_FETCH_RESOURCE",

  //Labeling

  FETCH_LABELING = "FETCH_LABELING",

  // System Mode

  FETCH_SYSTEM_MODE = "FETCH_SYSTEM_MODE",

  // Routing

  NAVIGATE_TO_SLIDE = "NAVIGATE_TO_SLIDE",
  DISPLAY_STATISTICS_SCREEN = "DISPLAY_STATISTICS_SCREEN",
  DISPLAY_CASE_REPORT = "DISPLAY_CASE_REPORT",
  RESET_ACTIVE_VIEW = "RESET_ACTIVE_VIEW",
  TOGGLE_CASE_REPORT = "TOGGLE_CASE_REPORT",
  SET_QUERY_PARAMS = "SET_QUERY_PARAMS",

  // Slides

  SET_SLIDE_WORKFLOW_STATE = "SET_SLIDE_WORKFLOW_STATE",
  HANDLE_SLIDE_WORKFLOW_STATE_CHANGE = "HANDLE_SLIDE_WORKFLOW_STATE_CHANGE",
  STORE_SLIDE_INSIGHTS = "STORE_SLIDE_INSIGHTS",
  DELETE_SLIDE_REPORT = "DELETE_SLIDE_REPORT",
  DELETE_SLIDE = "DELETE_SLIDE",

  // Cases

  FETCH_SR_CASES = "FETCH_SR_CASES",
  CONSTRUCT_CASES_DATA = "CONSTRUCT_CASES_DATA",
  FETCH_CASES_DATA = "FETCH_CASES_DATA",
  SET_CASE_WORKFLOW_STATE = "SET_CASE_WORKFLOW_STATE",
  SET_ACTIVE_CASE = "SET_ACTIVE_CASE",

  // Config

  SET_CONFIG = "SET_CONFIG",
  FETCH_HEATMAPS_CONFIG = "FETCH_HEATMAPS_CONFIG",

  // Error

  SET_ERROR = "SET_ERROR",

  // Heatmaps

  TOGGLE_HEATMAP = "TOGGLE_HEATMAP",
  CLEAR_ALL_HEATMAPS = "CLEAR_ALL_HEATMAPS",
}

export const actions: ActionTree<RootState, RootState> = {
  // Core
  async [reportsActions.GET_OR_FETCH_RESOURCE](
    context: Context,
    payload: { resourceName: string; uri: string }
  ): Promise<any> {
    try {
      if (context.state.hasOwnProperty(payload.resourceName)) {
        log(`Requested resource ${payload.resourceName} found in store`);
        // @ts-ignore
        return context.state[payload.resourceName];
      }
      log(
        `Requested resource ${payload.resourceName} not found in store. Fetching from ${payload.uri}`
      );
      const response = await GET(payload.uri);
      log(`Successfully fetched ${response.data}`);
    } catch (error) {
      log(`Failed to get ${payload.resourceName}. Reason: `, error);
    }
  },

  // System Mode

  async [RootActions.FETCH_SYSTEM_MODE](context: Context): Promise<SystemMode | undefined> {
    try {
      log(`Attempting to fetch system mode`);
      const response = await GET("system_mode");
      log(`successfully fetched system mode: ${response.data.systemMode}`);
      context.commit(RootMutations.SET_SYSTEM_MODE, {
        systemMode: response.data.systemMode,
      });
      return response.data.systemMode;
    } catch (error) {
      log(`failed to fetch system mode`);
    }
  },

  // Labeling
  async [RootActions.FETCH_LABELING](context: Context): Promise<any | undefined> {
    //
    try {
      log(`Attempting to fetch labeling`);
      const response = await GET("labeling");
      log(`successfully fetched labeling: ${JSON.stringify(response.data.data)}`);
      context.commit(RootMutations.SET_LABELING, {
        labeling: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      log(`failed to fetch labeling`);
    }
  },

  // Routing

  [RootActions.NAVIGATE_TO_SLIDE](
    context: Context,
    payload: { slide: ISlide; alertIndex: number }
  ) {
    try {
      log(`navigating to slide ${payload.slide.slideId}`);
      EventBus.emit("event-bridge-api", {
        eventName: "slide-selected-from-vue",
        data: {
          slide: payload.slide,
          alertIndex: payload.alertIndex,
        },
      });
      if (context.rootGetters.IS_FIRST_READ) {
        return context.dispatch(RootActions.STORE_SLIDE_INSIGHTS, {
          slideId: payload.slide.slideId,
        });
      }
    } catch (error) {
      log(`failed navigating to slide`, error);
    }
  },

  [RootActions.DISPLAY_STATISTICS_SCREEN](context: Context): void {
    log(`displaying statistics screen`);
    context.commit(RootMutations.DISPLAY_STATS_SCREEN);
  },

  [RootActions.DISPLAY_CASE_REPORT](context: Context): void {
    log(`displaying case report`);
    context.commit(RootMutations.DISPLAY_CASE_REPORT);
  },

  [RootActions.RESET_ACTIVE_VIEW](context: Context): void {
    log(`resetting active view`);
    context.commit(RootMutations.RESET_ACTIVE_VIEW);
  },

  [RootActions.TOGGLE_CASE_REPORT](context: Context): void {
    log("toggling case report");
    if (context.state.activeView && context.state.activeView === ActiveView.CaseReport) {
      context.dispatch(RootActions.RESET_ACTIVE_VIEW);
    } else {
      context.dispatch(RootActions.DISPLAY_CASE_REPORT);
    }
  },

  [RootActions.SET_QUERY_PARAMS](context: Context, payload: { queryParams: URLSearchParams }) {
    context.commit(RootMutations.SET_QUERY_PARAMS, payload);
  },

  // Slides
  async [RootActions.SET_SLIDE_WORKFLOW_STATE](
    context: Context,
    payload: { id: string; workflowState: WorkflowState }
  ): Promise<any> {
    const requestBody = { State: payload.workflowState };
    try {
      log(`updating GAPI's slide ${payload.id} report as ${payload.workflowState}`);
      await context.dispatch(`reportsModule/${reportsActions.UPDATE_REPORT}`);
      log(`updating slide ${payload.id} state to ${payload.workflowState}`);
      const response = await PUT(`slides/${payload.id}/state`, requestBody);
      context.commit(RootMutations.UPDATE_SLIDE, {
        slideId: payload.id,
        updates: { workflowState: response.data.workflowState },
      });
      log(`successfully updated slide ${payload.id} state to ${payload.workflowState}`);
      return Promise.resolve(response.data.workflowState);
    } catch (error) {
      log(`failed to updated slide state`);
    }
  },

  async [RootActions.HANDLE_SLIDE_WORKFLOW_STATE_CHANGE](
    context: Context,
    payload: { id: string; workflowState: WorkflowState }
  ): Promise<any> {
    try {
      const slide = context.getters.GET_SLIDE_BY_ID(payload.id);
      const slideWorkflowState = slide?.workflowState;
      const activeCase = context.getters.GET_ACTIVE_CASE;
      if (slideWorkflowState === payload.workflowState) {
        log(
          `slide ${payload.id} workflow state is already ${payload.workflowState}. No changes were made`
        );
        return;
      } else {
        const newWorkflowState = await context.dispatch(RootActions.SET_SLIDE_WORKFLOW_STATE, {
          id: payload.id,
          workflowState: payload.workflowState,
        });
        if (
          activeCase.workflowState === WorkflowState.COMPLETE &&
          slideWorkflowState === WorkflowState.COMPLETE &&
          newWorkflowState !== WorkflowState.COMPLETE
        ) {
          log(
            `automatically setting case ${context.state.activeCaseId} workflow state to INCOMPLETE`
          );
          await context.dispatch(RootActions.SET_CASE_WORKFLOW_STATE, {
            id: activeCase.caseId,
            workflowState: WorkflowState.INCOMPLETE,
          });
          log(
            `automatically setting case ${context.state.activeCaseId} is complete status to false`
          );
          await context.dispatch(`reportsModule/${reportsActions.UPDATE_CASE_COMPLETENESS}`, {
            caseId: activeCase.caseId,
            isComplete: false,
          });
        }
      }
    } catch (error) {
      log(`an error has occurred while attempting to update the workflow state. ${error}`);
    }
  },

  async [RootActions.STORE_SLIDE_INSIGHTS](
    context: Context,
    payload: { slideId: string }
  ): Promise<any> {
    try {
      log(`fetching slide ${payload.slideId} insights`);
      const response = await GET(`slides/${payload.slideId}/insights`);
      context.commit(RootMutations.UPDATE_SLIDE, {
        slideId: payload.slideId,
        updates: { insights: response.data.data },
      });
      log(
        `successfully fetched ${response.data.data.length} insights for slide ${payload.slideId}`
      );
      return Promise.resolve(response.data.data);
    } catch (error) {
      log(`failed to fetch slide ${payload.slideId} insights`);
    }
  },

  async [RootActions.DELETE_SLIDE_REPORT](
    context: Context,
    payload: { slideId: string }
  ): Promise<any> {
    try {
      log(`deleting slide ${payload.slideId}`);
      // step1: Try to clear the slide report
      // 1. Remove slide report from case report
      await context.dispatch(`reportsModule/${reportsActions.DELETE_SLIDE_REPORT}`, {
        slideId: payload.slideId,
      });
      // 2. update case report
      await context.dispatch(`reportsModule/${reportsActions.UPDATE_REPORT}`);
    } catch (error) {
      log(`failed to delete slide report ${payload.slideId}`, error);
      throw error;
    }
  },

  [RootActions.DELETE_SLIDE](
    context: Context,
    payload: {
      slideId: string;
    }
  ): Promise<any> {
    // Try to delete slide from GAPI
    return DELETE(`slides/${payload.slideId}`);
  },

  // Cases

  // - SR
  async [RootActions.FETCH_SR_CASES](context: Context): Promise<any> {
    try {
      log("Fetching cases");
      const response = await GET("cases");
      log(`successfully fetched cases`);
      context.commit(RootMutations.SET_SR_CASES, {
        cases: response.data.data,
      });
      return response.data.data;
    } catch (error) {
      log(`failed fetching cases.`);
    }
  },

  // - FR
  async [RootActions.CONSTRUCT_CASES_DATA](context: Context): Promise<any> {
    try {
      const [cases, labels, insightsConfig] = await context.dispatch(RootActions.FETCH_CASES_DATA);
      context.commit(RootMutations.SET_INSIGHTS_CONFIG, { insightsConfig });
      const caseLabels = constructAllLabels(cases, labels, insightsConfig);
      context.commit(RootMutations.SET_FR_CASES, { cases, caseLabels });
    } catch (error) {
      log(`failed constructing cases data`);
    }
  },

  async [RootActions.FETCH_HEATMAPS_CONFIG](context: Context): Promise<any> {
    try {
      log(`fetching heatmaps config`);
      const heatmapsConfig = await GET("config/heatmaps");
      log(`setting heatmaps config`, heatmapsConfig.data.data);
      context.commit(RootMutations.SET_HEATMAPS_CONFIG, {
        heatmapsConfig: heatmapsConfig.data.data,
      });
    } catch (error) {
      log(`failed to fetch cases data`);
    }
  },

  async [RootActions.FETCH_CASES_DATA](): Promise<any> {
    try {
      log(`fetching all cases, labels, and insights config`);
      const cases = GET("cases");
      const labels = GET("cases/labels");
      const insightsConfig = GET("config/insights");
      const response = await Promise.all([cases, labels, insightsConfig]);
      return response.map((resource) => resource.data.data);
    } catch (error) {
      log(`failed to fetch cases data`);
    }
  },

  async [RootActions.SET_CASE_WORKFLOW_STATE](
    context: Context,
    payload: { id: string; workflowState: WorkflowState }
  ): Promise<void> {
    const requestBody = { State: payload.workflowState };
    try {
      log(`updating case ${payload.id} state to ${payload.workflowState}`);
      const response = await PUT(`cases/${payload.id}/state`, requestBody);
      context.commit(RootMutations.UPDATE_CASE, {
        caseId: payload.id,
        updates: { workflowState: response.data.workflowState },
      });
      log(`successfully updated case ${payload.id} state to ${payload.workflowState}`);
    } catch (error) {
      log(`failed to updated case state`);
    }
  },

  [RootActions.SET_ACTIVE_CASE](context: Context, payload: { id: string }): void {
    log("Setting active case to", payload.id);
    context.commit(RootMutations.SET_ACTIVE_CASE_ID, { id: payload.id });
  },

  // Config

  [RootActions.SET_CONFIG](context: Context, payload: { config: any }): void {
    log("Setting configuration", payload.config);
    context.commit(RootMutations.SET_CONFIG, { config: payload.config });
  },

  // Error

  [RootActions.SET_ERROR](context: Context, payload: { errorMsg: string }): void {
    log("Setting error", payload?.errorMsg);
    context.commit(RootMutations.SET_ERROR);
  },

  // Heatmaps

  [RootActions.TOGGLE_HEATMAP](context: Context, payload: { heatmapConfig: IHeatmapConfig }): void {
    context.commit("toggleHeatmap", payload.heatmapConfig);
  },

  [RootActions.CLEAR_ALL_HEATMAPS](context: Context) {
    context.commit("clearAllHeatmaps");
  },
};
